import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { useStoreBase } from "@/lib/store";
import { ArrowRight } from "lucide-react";
import { formatBlogDate } from "@/lib/storeBlog";

function CardFace({ post, base }) {
  return (
    <Link
      to={`${base}/blog/${post.slug}`}
      className="w-[300px] sm:w-[340px] shrink-0 bg-white rounded-[22px] overflow-hidden border border-stone-100 shadow-[0_12px_40px_rgba(15,23,42,0.08)] hover:shadow-[0_16px_48px_rgba(15,23,42,0.12)] transition-shadow"
      data-testid={`home-blog-card-${post.slug}`}
    >
      <div className="h-[168px] sm:h-[184px] bg-stone-100 overflow-hidden">
        {post.featured_image_url ? (
          <img src={mediaUrl(post.featured_image_url)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-sky-100 to-indigo-100" />
        )}
      </div>
      <div className="p-5">
        <div className="text-xs font-medium text-sky-600">{formatBlogDate(post.published_at)}</div>
        <h3 className="mt-2 font-display font-bold text-[17px] leading-snug text-stone-900 line-clamp-2 min-h-[2.6em]">{post.title}</h3>
        <p className="mt-2 text-sm text-stone-500 leading-relaxed line-clamp-3 min-h-[3.9em]">{post.excerpt}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600">
          Read more <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

export default function StorefrontBlogSection({ config }) {
  const { slug, base } = useStoreBase();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const settings = config?.store_blog;
  const active = Boolean(config?.store_blog_active && settings?.show_on_homepage);

  const homePath = String(base || "").replace(/\/+$/, "") || "";
  const path = String(location.pathname || "/").replace(/\/+$/, "") || "/";
  const isHome = homePath ? path === homePath : path === "/";

  useEffect(() => {
    if (!active || !isHome || !slug) return;
    api.get(`/store/${slug}/blogs`).then(({ data }) => setPosts(Array.isArray(data) ? data : [])).catch(() => setPosts([]));
  }, [active, isHome, slug]);

  const loop = useMemo(() => {
    if (!posts.length) return [];
    return [...posts, ...posts];
  }, [posts]);

  if (!active || !isHome || !posts.length) return null;

  const heading = settings.heading || "Latest Stories";
  const duration = Math.max(24, posts.length * 12);

  return (
    <section className="bg-[#F6F7F9] py-14 sm:py-16 overflow-hidden" data-testid="home-blog-section">
      <h2 className="text-center font-display font-extrabold text-2xl sm:text-3xl text-stone-900">{heading}</h2>
      <div className="mt-10 relative">
        <div className="overflow-hidden">
          <div
            className="store-blog-track flex gap-6 w-max px-4"
            style={{ animationDuration: `${duration}s` }}
            data-testid="home-blog-track"
          >
            {loop.map((p, i) => (
              <CardFace key={`${p.id}-${i}`} post={p} base={base} />
            ))}
          </div>
        </div>
      </div>
      <div className="mt-10 flex justify-center">
        <Link
          to={`${base}/blog`}
          className="inline-flex items-center gap-2 h-11 px-7 rounded-full border-2 border-sky-400 text-sky-600 font-semibold bg-white hover:bg-sky-50"
          data-testid="home-blog-read-all"
        >
          Read All <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <style>{`
        @keyframes store-blog-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .store-blog-track {
          animation: store-blog-marquee linear infinite;
        }
        .store-blog-track:hover { animation-play-state: paused; }
      `}</style>
    </section>
  );
}
